import { Grid } from '../core/Grid.js';
import {
    Position,
    Direction,
    positionKey,
    parsePositionKey,
    edgeKey,
} from '../core/GridGeometry.js';

// ── Types ────────────────────────────────────────────────────────

export interface PathCollectionResult {
    gateCells: Set<string>;    // All cells on all gate paths (barriers for flood-fill)
    gateLinks: Set<string>;    // Edge keys between consecutive cells on gate paths
}

export interface FloodFillResult {
    cellRegion: Map<string, number>;
    regionCells: Map<number, Set<string>>;
    regionDigits: Map<number, number[]>;
    regionCount: number;
}

export interface DigitTreeResult {
    edges: Set<string>;
    connected: boolean;
}

// ── Phase 1: Collect Gate Cells from Paths ──────────────────────

/**
 * Collect gate cells and gate link edges from completed gate paths.
 * Each path is an ordered array of positions where first and last are gates.
 */
export function collectGateCellsFromPaths(
    grid: Grid,
    gatePaths: Map<string, Position[]>,
): PathCollectionResult {
    const gateCells = new Set<string>();
    const gateLinks = new Set<string>();

    for (const [, path] of gatePaths) {
        for (let i = 0; i < path.length; i++) {
            gateCells.add(positionKey(path[i]));
            if (i > 0) {
                const edge = grid.geometry.createEdgeFromCells(path[i - 1], path[i]);
                if (edge) gateLinks.add(edgeKey(edge));
            }
        }
    }

    return { gateCells, gateLinks };
}

// ── Phase 2: Flood-fill Regions ──────────────────────────────────

const DIRECTIONS: Direction[] = ['N', 'S', 'E', 'W'];

/**
 * Grid 4-adjacency flood-fill on cells NOT in gateCells.
 * Groups cells into regions; tracks which digits land in each region.
 */
export function floodFillRegions(
    grid: Grid,
    gateCells: Set<string>,
): FloodFillResult {
    const cellRegion = new Map<string, number>();
    const regionCells = new Map<number, Set<string>>();
    const regionDigits = new Map<number, number[]>();
    let regionId = 0;

    for (let row = 0; row < grid.size; row++) {
        for (let col = 0; col < grid.size; col++) {
            const key = positionKey({ row, col });
            if (gateCells.has(key)) continue;
            if (cellRegion.has(key)) continue;

            // BFS flood-fill from this cell
            const cells = new Set<string>();
            const digits: number[] = [];
            const queue: Position[] = [{ row, col }];
            cellRegion.set(key, regionId);
            cells.add(key);

            while (queue.length > 0) {
                const pos = queue.shift()!;
                const digit = grid.getDigitAt(pos);
                if (digit !== null) {
                    digits.push(digit);
                }

                for (const dir of DIRECTIONS) {
                    const neighbor = grid.geometry.moveInside(pos, dir);
                    if (!neighbor) continue;
                    const nKey = positionKey(neighbor);
                    if (gateCells.has(nKey)) continue;
                    if (cellRegion.has(nKey)) continue;
                    cellRegion.set(nKey, regionId);
                    cells.add(nKey);
                    queue.push(neighbor);
                }
            }

            regionCells.set(regionId, cells);
            if (digits.length > 0) {
                regionDigits.set(regionId, digits);
            }
            regionId++;
        }
    }

    return { cellRegion, regionCells, regionDigits, regionCount: regionId };
}

// ── Phase 3: Digit Tree ─────────────────────────────────────────

/**
 * Build a minimal BFS tree connecting all digits within a region.
 * Uses iterative nearest-digit: BFS from tree cells to find nearest
 * unconnected digit, trace path back, repeat.
 */
export function buildDigitTree(
    grid: Grid,
    regionCells: Set<string>,
    digits: number[],
): DigitTreeResult {
    const edges = new Set<string>();
    if (digits.length <= 1) return { edges, connected: true };

    // Get digit positions
    const digitPositions = new Map<string, number>();
    for (const d of digits) {
        const pos = grid.getDigitPosition(d);
        if (pos) digitPositions.set(positionKey(pos), d);
    }

    const firstDigit = digits[0];
    const firstPos = grid.getDigitPosition(firstDigit);
    if (!firstPos) return { edges, connected: false };

    const treeCells = new Set<string>();
    treeCells.add(positionKey(firstPos));
    const connectedDigits = new Set<string>();
    connectedDigits.add(positionKey(firstPos));

    const remainingDigits = new Set<string>();
    for (const [key] of digitPositions) {
        if (!connectedDigits.has(key)) remainingDigits.add(key);
    }

    while (remainingDigits.size > 0) {
        // Multi-source BFS from all tree cells
        const visited = new Set<string>(treeCells);
        const parent = new Map<string, string>();
        const queue = [...treeCells];

        let found: string | null = null;
        while (queue.length > 0 && found === null) {
            const currentKey = queue.shift()!;
            const currentPos = parsePositionKey(currentKey);

            for (const dir of DIRECTIONS) {
                const neighbor = grid.geometry.moveInside(currentPos, dir);
                if (!neighbor) continue;
                const nKey = positionKey(neighbor);
                if (!regionCells.has(nKey)) continue;
                if (visited.has(nKey)) continue;

                visited.add(nKey);
                parent.set(nKey, currentKey);

                if (remainingDigits.has(nKey)) {
                    found = nKey;
                    break;
                }
                queue.push(nKey);
            }
        }

        if (found === null) break; // Remaining digits unreachable

        // Trace path back to tree, collect edges and cells
        let cell = found;
        while (parent.has(cell)) {
            const prev = parent.get(cell)!;
            const posA = parsePositionKey(cell);
            const posB = parsePositionKey(prev);
            const edge = grid.geometry.createEdgeFromCells(posA, posB);
            if (edge) edges.add(edgeKey(edge));
            treeCells.add(cell);
            cell = prev;
        }

        // Mark found digit as connected; also check for other digits along the path
        remainingDigits.delete(found);
        for (const treeCell of treeCells) {
            if (remainingDigits.has(treeCell)) {
                remainingDigits.delete(treeCell);
                connectedDigits.add(treeCell);
            }
        }
        connectedDigits.add(found);
    }

    return { edges, connected: remainingDigits.size === 0 };
}
